import React, { useState } from "react";
import { Button, Iconography, IconographySizes, Loader, LoaderSizes } from "@hid-galaxy-ui/galaxy-react";
import ChatItem, { Chat } from "../ChatItem";
import "./ChatList.scss";

interface GroupedChats {
  today: Chat[];
  yesterday: Chat[];
  last_7_days: Chat[];
  older: Chat[];
}

interface ChatListProps {
  chats: Chat[];
  groupedChats?: GroupedChats;
  activeChatId?: string;
  onChatSelect: (chatId: string) => void;
  onNewChat: () => void;
  onRefresh?: () => void;
  isLoading?: boolean;
}

const ChatList: React.FC<ChatListProps> = ({
  chats,
  groupedChats,
  activeChatId,
  onChatSelect,
  onNewChat,
  onRefresh,
  isLoading = false,
}) => {
  // State to track which sections are expanded (default: all expanded)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    today: true,
    yesterday: false,
    last_7_days: false,
    older: false,
  });

  // Toggle section expand/collapse
  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  // Check if grouped chats are available
  const hasGroupedChats = !!groupedChats;

  // Check if all sections are empty
  const allSectionsEmpty = groupedChats && (
    groupedChats.today.length === 0 &&
    groupedChats.yesterday.length === 0 &&
    groupedChats.last_7_days.length === 0 &&
    groupedChats.older.length === 0
  );

  const renderChatSection = (title: string, sectionChats: Chat[], sectionKey: string) => {
    const isExpanded = expandedSections[sectionKey] ?? true;

    return (
      <div key={title} className="chat-list-section">
        <div 
          className="chat-list-section-title"
          onClick={() => toggleSection(sectionKey)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleSection(sectionKey);
            }
          }}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? "Collapse" : "Expand"} ${title} section`}
        >
          <div className="chat-list-section-title-content">
            <Iconography 
              icon={isExpanded ? "chevronDown" : "chevronRight"} 
              size={IconographySizes.Small}
              className="chat-list-section-chevron"
            />
            <h3>{title}</h3>
          </div>
        </div>
        {isExpanded && (
          <div className="chat-list-section-items">
            {sectionChats.length === 0 ? (
              <div className="chat-list-section-empty">
                <p>No chats in this section</p>
              </div>
            ) : (
              sectionChats.map((chat) => (
                <ChatItem
                  key={chat.id}
                  chat={{
                    ...chat,
                    isActive: chat.id === activeChatId,
                  }}
                  onClick={() => onChatSelect(chat.id)}
                />
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGroupedChats = () => {
    if (!groupedChats) return null;

    return (
      <>
        {renderChatSection("Today", groupedChats.today, "today")}
        {renderChatSection("Yesterday", groupedChats.yesterday, "yesterday")}
        {renderChatSection("Last 7 Days", groupedChats.last_7_days, "last_7_days")}
        {renderChatSection("Older", groupedChats.older, "older")}
        {allSectionsEmpty && !isLoading && (
          <div className="chat-list-empty">
            <p>No chats yet. Start a new conversation!</p>
          </div>
        )}
      </>
    );
  };

  const renderFlatChats = () => {
    if (chats.length === 0) {
      return (
        <div className="chat-list-empty">
          <p>No chats yet. Start a new conversation!</p>
        </div>
      );
    }

    return (
      <div className="chat-list-items">
        {chats.map((chat) => (
          <ChatItem
            key={chat.id}
            chat={{
              ...chat,
              isActive: chat.id === activeChatId,
            }}
            onClick={() => onChatSelect(chat.id)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="chat-list">
      <div className="chat-list-header">
        <h2 className="chat-list-title">Chats</h2>
        {onRefresh && (
          <Button
            variant="flat"
            icon={
              <Iconography 
                icon="arrowRotateRight" 
                size={IconographySizes.Small} 
              />
            }
            onClick={onRefresh}
            aria-label="Refresh chat history"
            disabled={isLoading}
          />
        )}
      </div>
      <div className="chat-list-new-button">
        <Button
          variant="primary"
          label="New Chat"
          icon={<Iconography icon="plus" size={IconographySizes.Small} />}
          onClick={onNewChat}
          aria-label="New Chat"
        />
      </div>
      {isLoading && !groupedChats ? (
        <div className="chat-list-loading">
          <Loader variant={LoaderSizes.medium} />
        </div>
      ) : isLoading && groupedChats && allSectionsEmpty ? (
        <div className="chat-list-loading">
          <Loader variant={LoaderSizes.medium} />
        </div>
      ) : hasGroupedChats ? (
        <div className="chat-list-grouped">
          {isLoading && (
            <div className="chat-list-loading-overlay">
              <Loader variant={LoaderSizes.medium} />
            </div>
          )}
          {renderGroupedChats()}
        </div>
      ) : (
        renderFlatChats()
      )}
    </div>
  );
};

export default ChatList;

